"use client";

import { createContext, useCallback, useContext, useTransition } from "react";
import { useRouter } from "next/navigation";
import Spinner from "./Spinner";

// App-wide "the next page is loading" indicator. Clicking a thumbnail/button
// that navigates (router.push/replace) used to show NOTHING on the current
// page while Next.js fetched/rendered the destination — for a route with any
// real work to do before its first paint (an auth check, a data fetch), that
// gap read as "did my click even register?" rather than "loading." The
// destination page's OWN loading states (see Spinner usage across the app)
// only appear once it has already mounted; this covers the gap BEFORE that.
//
// useTransition's isPending stays true for exactly as long as the navigation
// is genuinely still resolving — React keeps this component (and the rest of
// the current page) mounted and marks the transition pending until the new
// route is ready to commit. A fast navigation's pending window is too brief
// to ever paint, so this only becomes visible for a navigation slow enough
// to actually need it — it doesn't add a flash to every click.
const NavContext = createContext(null);

export function NavigationProvider({ children }) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const push = useCallback((href, options) => {
    startTransition(() => { router.push(href, options); });
  }, [router]);

  const replace = useCallback((href, options) => {
    startTransition(() => { router.replace(href, options); });
  }, [router]);

  return (
    <NavContext.Provider value={{ push, replace, isPending }}>
      {children}
      {isPending && (
        <div
          role="status"
          aria-label="Loading"
          style={{
            position: "fixed", inset: 0, zIndex: 99999,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            // Never blocks a second click/cancel — this is feedback, not a
            // modal, and a navigation that's already in flight can't be
            // meaningfully interrupted by clicking through it anyway.
            pointerEvents: "none",
          }}
        >
          <Spinner size={40} />
        </div>
      )}
    </NavContext.Provider>
  );
}

// Drop-in replacement for the push/replace half of useRouter() — keep using
// useRouter() directly in the same file for anything else (back(), refresh(),
// prefetch()), which aren't navigations this overlay needs to cover.
export function useNav() {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error("useNav() must be used within <NavigationProvider>.");
  return ctx;
}
